/**
 * FocusToggle — a 28px round button centred on the first
 * splitter that collapses the AI review column so the video fills the panel.
 * The chevrons rotate 180° once collapsed and the tooltip flips to the other
 * side of the button, because in focus mode the button sits at the far left.
 */
import type { MouseEvent as ReactMouseEvent } from 'react';

import { CaretDoubleRight } from '../../../../../design-system/icons';
import { cn } from '../../../../../lib/utils';
import { FOCUS_TOGGLE_ATTRIBUTE } from './focusToggleAttribute';

const CHEVRON_SIZE_PX = 14;
/** Level with the first row of column content. */
const TOGGLE_TOP_PX = 22;
/** Clearance from the panel edge when the splitter itself is the last column. */
const TOGGLE_EDGE_INSET_PX = 6;

const BUTTON_CLASSES =
  'group/toggle absolute z-[8] inline-flex size-7 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-[0_1px_2px_rgba(23,23,23,0.06),0_2px_6px_rgba(23,23,23,0.05)] transition-colors duration-150 hover:border-foreground hover:bg-foreground hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand-500)';
/** Centred on the 6px bar. */
const BUTTON_CENTERED_CLASSES = 'left-1/2 -translate-x-1/2';
const TOOLTIP_CLASSES =
  'pointer-events-none absolute top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-foreground px-[9px] py-1 text-caption-medium text-white opacity-0 transition-opacity duration-150 group-hover/toggle:opacity-100';

const COLLAPSE_LABEL = 'Collapse review panel';
const EXPAND_LABEL = 'Expand review panel';
const COLLAPSE_TOOLTIP = 'Collapse review';
const EXPAND_TOOLTIP = 'Show review';

/** Named for what the click will do. */
function toggleLabel(focused: boolean): string {
  return focused ? EXPAND_LABEL : COLLAPSE_LABEL;
}

function tooltipText(focused: boolean): string {
  return focused ? EXPAND_TOOLTIP : COLLAPSE_TOOLTIP;
}

export interface FocusToggleProps {
  /** True while the AI review column is collapsed. */
  focused: boolean;
  /**
   * The splitter hosting this button is the panel's last column — which happens
   * in focus mode with the conversation closed. Centring
   * a 28px button on that 6px bar would push half of it past the body's edge,
   * and the notes-mode body is `overflow-hidden`, so that half would be clipped
   * and unclickable. Tuck it inside instead.
   */
  atPanelEdge?: boolean;
  onToggle(): void;
}

export function FocusToggle({ focused, atPanelEdge = false, onToggle }: FocusToggleProps) {
  // The button lives inside the splitter; a click here must never read as the
  // start of a drag or as the splitter's double-click reset.
  const handleClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onToggle();
  };

  return (
    <button
      type="button"
      {...{ [FOCUS_TOGGLE_ATTRIBUTE]: '' }}
      aria-label={toggleLabel(focused)}
      aria-expanded={!focused}
      className={cn(BUTTON_CLASSES, !atPanelEdge && BUTTON_CENTERED_CLASSES)}
      style={atPanelEdge ? { top: TOGGLE_TOP_PX, right: TOGGLE_EDGE_INSET_PX } : { top: TOGGLE_TOP_PX }}
      onClick={handleClick}
    >
      <CaretDoubleRight size={CHEVRON_SIZE_PX} className={cn('transition-transform duration-200', focused && 'rotate-180')} />
      {/* In focus mode the button sits at the panel's right-hand side, so a
          tooltip to its right would run off the edge — flip it to the left. */}
      <span className={cn(TOOLTIP_CLASSES, focused ? 'right-[calc(100%+8px)]' : 'left-[calc(100%+8px)]')}>
        {tooltipText(focused)}
      </span>
    </button>
  );
}
